import { useState, useEffect, useRef } from "react";
import axiosInstance from '../axiosInstance';

export const useProductDetailsApproval = ({ productDetailsId, requestId, persona = null }) => {
  const checkLink = persona === "customer" ? "check/enduser" : "check";
  
  // state for managing approval status
  const [isApproved, setIsApproved] = useState(false);
  const [isCheckingApproval, setIsCheckingApproval] = useState(true);
  
  // state for api operations
  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  
  // Add a refresh counter to force re-fetch
  const [refreshCounter, setRefreshCounter] = useState(0);
  
  // fetch approval status on mount and when dependencies change
  useEffect(() => {
    const fetchApproval = async () => {
      if (!productDetailsId || !requestId) {
        setIsCheckingApproval(false);
        setIsApproved(false);
        return;
      }

      setIsCheckingApproval(true);
      
      try {
        const response = await axiosInstance.get(`/api/approve-product-details/${checkLink}`, {
          params: { 
            request_id: requestId,
            product_details_id: productDetailsId
          }
        });
        
        const newApprovalStatus = (response.data && response.data.is_approved !== null && response.data.is_approved !== undefined) 
          ? response.data.is_approved 
          : false;
        
        // Only update if the value actually changed
        setIsApproved(prev => prev === newApprovalStatus ? prev : newApprovalStatus);
      } catch (error) {
        console.error("error checking approval:", error);
        setIsApproved(false);
      } finally {
        setIsCheckingApproval(false);
      }
    };

    fetchApproval();
    
    // Listen for approval change events from other instances
    const handleApprovalChange = (event) => {
      if (event.detail.productDetailsId === productDetailsId && event.detail.requestId === requestId) {
        // Only refetch if the new status is different from current
        setIsApproved(prev => {
          if (prev !== event.detail.approved) {
            return event.detail.approved;
          }
          return prev;
        });
      }
    };
    
    window.addEventListener('productDetailsApprovalChanged', handleApprovalChange);
    
    return () => {
      window.removeEventListener('productDetailsApprovalChanged', handleApprovalChange);
    };
  }, [productDetailsId, requestId, checkLink, refreshCounter]);

  const approveProductDetails = async () => {
    if (!productDetailsId || !requestId) {
      const error = new Error("something went wrong.");
      setApprovalError(error);
      console.error(error);
      return false;
    }

    setIsApproving(true);
    setApprovalError(null);

    try {
      const response = await axiosInstance.post(`/api/approve-product-details/approve`, {
         product_details_id: productDetailsId,
         request_id: requestId
      });

      if (response.status === 200 || response.status === 201) {
        setIsApproved(true);
        // Notify other hook instances
        window.dispatchEvent(new CustomEvent('productDetailsApprovalChanged', {
          detail: { productDetailsId, requestId, approved: true }
        }));
        return true;
      }
      return false;
    } catch (error) {
      setApprovalError(error);
      return false;
    } finally {
      setIsApproving(false);
    }
  };

  const deleteApproval = async () => {
    if (!productDetailsId || !requestId) {
      const error = new Error("something went wrong.");
      setApprovalError(error);
      console.error(error);
      return false;
    }

    setIsDeleting(true);
    setApprovalError(null);

    try {
      const response = await axiosInstance.delete(`/api/approve-product-details/delete`, {
        data: {
          product_details_id: productDetailsId,
          request_id: requestId
        }
      });

      if (response.status === 200) {
        setIsApproved(false);
        // Notify other hook instances
        window.dispatchEvent(new CustomEvent('productDetailsApprovalChanged', {
          detail: { productDetailsId, requestId, approved: false }
        }));
        return true;
      }
      return false;
    } catch (error) {
      setApprovalError(error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    // state
    isApproved,
    isCheckingApproval,
    isApproving,
    isDeleting,
    approvalError,
    
    // actions
    approveProductDetails,
    deleteApproval,
  };
};

